<template>
  <section class="destinations-silo">
    <div class="main-content wide destinations-wrap">
      <div class="wrap contain">
        <div class="title-wrap">
          <section>
            <h1 v-dompurify-html="getPageDataItem('title')" />
            <div
              v-dompurify-html="getPageDataItem('description')"
              class="description"
            />
          </section>

          <div class="selector-wrap">
            <div
              class="filters-trigger"
              @click="openFilters"
            >
              <span class="icon filter"></span>
              {{ $t('destinationsSilo.showFilters') }}
            </div>
            <div class="selector-tab">
              <div
                class="item map"
                :class="{ active: showMap }"
                @click="switchView('map')"
              >
                <span class="icon pin" />
                <span class="selector-text">{{ $t('destinationsSilo.mapView') }}</span>
              </div>
              <div
                class="item list"
                :class="{ active: activeView === 'list' }"
                @click="switchView('list')"
              >
                <span class="icon list" />
                <span class="selector-text">{{ $t('destinationsSilo.listView') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-show="!showMap"
        class="main-content wide"
      >
        <div class="wrap">
          <section class="content-wrap">
            <div
              ref="filterGroup"
              class="filter-group"
            >
              <div class="filter-wrapper">
                <span class="icon filter"></span>
                {{ $t('destinationsSilo.filters') }}
              </div>
              <div class="wrap">
                <filters class="desktop-filters" />
              </div>
            </div>
            <div
              class="destination-container"
              :class="{ map: showMap }"
            >
              <div>
                <template v-for="(destination, index) in filteredDestinations">
                  <destination
                    v-if="destination.code.slice(0, 3).toUpperCase() !== airport.code"
                    :key="destination.origin + index + destination.code"
                    :origin="airport.code"
                    :labels="destination.labels"
                    :origin-type="airport.type"
                    :destination="destination"
                  />
                </template>
              </div>
            </div>
          </section>

          <div
            v-if="termsAndConditionsVisible"
            class="wrap terms"
          >
            <terms-and-conditions
              :title="termsAndConditions.title"
              :content="termsAndConditions.content"
            />
          </div>
        </div>
      </div>

      <section
        v-show="showMap"
        class="map-with-filters"
      >
        <route-map />
        <div class="map-content-wrap">
          <div class="inner">
            <div
              class="floating-filters"
              :class="{ 'open filter-group': isMapFiltersVisible }"
            >
              <template v-if="isMapFiltersVisible">
                <div class="filter-wrapper">
                  <section class="filters-title-wrap">
                    <span class="icon filter"></span>
                    {{ $t('destinationsSilo.filters') }}
                  </section>

                  <span
                    v-tooltip="{
                      content: $t('destinationsSilo.tooltipContent'),
                      position: 'top',
                      class: 'js-calendar-tooltip'
                    }"
                    class="info-icon"
                  />

                  <close-icon @click="toggleFilters" />
                </div>
                <div class="wrap">
                  <filters class="desktop-filters" />
                </div>
              </template>

              <template v-else>
                <div
                  class="filters-trigger"
                  @click="toggleFilters"
                >
                  <span class="filter"></span>
                  {{ $t('destinationsSilo.showFilters') }}
                </div>
              </template>
            </div>

            <div
              v-if="chosenDestination.code && isDrawerOpen"
              class="destination-drawer"
            >
              <map-info-block
                :key="chosenDestination.code"
                :destination="chosenDestination"
              />
            </div>
          </div>
        </div>
      </section>
      <p v-if="showMap" class="wrap contain">{{ $t('destinationsSilo.tooltipContent') }}</p>
    </div>
  </section>
</template>

<script>
  import { mapState } from 'vuex'
  import Destination from 'components/DestinationsTemplate/Destination'
  import Filters from 'components/DestinationsTemplate/Filters'
  import RouteMap from 'containers/Pages/Map/Map'
  import TermsAndConditions from 'components/TermsAndConditions'
  import UrlParams from 'utils/UrlParams'
  import Tooltip from 'utils/directives/Tooltip'

  export default {
    directives: {
      Tooltip
    },
    components: {
      Destination,
      TermsAndConditions,
      RouteMap,
      Filters,
      CloseIcon: () => import(/* webpackChunkName: "close-icon" */ 'components/CloseIcon'),
      MapInfoBlock: () => import(/* webpackChunkName: "map-info-block" */ 'components/DestinationsTemplate/MapInfoBlock')
    },
    data() {
      return {
        activeView: 'map',
        isMapFiltersVisible: true,
        isDrawerOpen: false,
        unsubscribe: () => null
      }
    },
    computed: {
      ...mapState({
        pageData: (state) => state.destinationsSilo.pageData,
        airport: (state) => state.destinationsSilo.airport,
        termsAndConditionsVisible: (state) => state.destinationsSilo.termsAndConditionsVisible,
        termsAndConditions: (state) => state.destinationsSilo.termsAndConditions,
        selectedCountry: (state) => state.destinationsSilo.selectedCountry,
        selectedLabel: (state) => state.destinationsSilo.selectedLabel,
        countryFromUrl: (state) => state.destinationsSilo.countryFromUrl,
        selectedDestination: (state) => state.destinationsSilo.selectedDestination
      }),
      destinations() {
        return this.$store.getters['destinationsSilo/destinations'] || []
      },
      showMap() {
        return this.activeView === 'map'
      },
      filteredDestinations() {
        if (this.countryFromUrl !== '') {
          return this.destinations.filter(
            ({ countryCode }) => countryCode === this.countryFromUrl
          )
        }

        if (this.selectedLabel !== '') {
          return this.destinations.filter(({ labels }) =>
            labels.includes(this.selectedLabel)
          )
        }

        if (this.selectedCountry?.value) {
          return this.destinations.filter(
            ({ countryCode }) => countryCode === this.selectedCountry?.value
          )
        }

        if (!this.selectedDestination) {
          return this.destinations
        }

        const destination = this.destinations.find(
          ({ code }) => code.slice(0, 3).toUpperCase() === this.selectedDestination?.code
        )

        const destinationsWithoutDestination = this.destinations.filter(
          ({ code }) => code !== destination?.code
        )

        return [destination, ...destinationsWithoutDestination]
      },
      chosenDestination() {
        if (!this.destinations.length || !this.selectedDestination?.code) {
          return {}
        }

        return this.destinations
          .map((dest) => ({ ...dest, code: dest?.code.slice(0, 3).toUpperCase() }))
          .find(({ code }) => code === this.selectedDestination?.code.toUpperCase())
      }
    },
    mounted() {
      this.$store.commit('footer/setDisableSubscribePopup', true)
      this.unsubscribe = this.$store.subscribe(({ type }) => {
        if (type !== 'destinationsSilo/selectDestination') {
          return
        }

        if (!this.selectedDestination) {
          this.isDrawerOpen = false

          return
        }

        this.isDrawerOpen = true
      })

      const mapParam = UrlParams.getUrlParam(window.location.href, 'map')

      if (mapParam === 'closed') {
        this.activeView = 'list'
      }

      this.doFetch()
      document.body.setAttribute('tabindex', -1)
    },
    beforeDestroy() {
      this.unsubscribe()
    },
    methods: {
      switchView(activeItem) {
        this.activeView = activeItem
      },
      toggleFilters() {
        this.isMapFiltersVisible = !this.isMapFiltersVisible
      },
      getPageDataItem(name) {
        return this.pageData[name] || ''
      },
      doFetch() {
        this.$store.commit('main/showPlaneLoader', true)

        this.$store
          .dispatch('destinationsSilo/fetchDestinations', true)
          .finally(() => this.$store.commit('main/showPlaneLoader', false))
      },
      openFilters() {
        this.$store.dispatch('main/openModal')
        this.$store.commit('main/setShowModalHeader', true)
        this.$store.commit('main/setShowModalHeaderWhite', true)
        this.$store.commit('main/setModalTitle', this.$t('destinationsSilo.filters'))
        this.$store.commit('main/setModalContent', Filters)
        this.$store.commit('main/setModalType', 'centered')
        this.$store.commit('main/setModalClass', 'modal-white-centered')
        this.$store.commit('main/setShowCloseIcon', true)
        this.$store.commit('main/setShowModalHeader', false)
        this.$store.commit('main/setModalCloseFn', this.closeFilters)
        this.$store.commit('main/setModalProps', {
          showCta: false
        })
      },
      closeFilters() {
        this.$store.dispatch('main/closeModal')
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import '~assets/styles/common.scss';

  .info-icon {
    color: $blue-medium;
    cursor: pointer;
    display: inline-block;

    &:hover {
      color: $blue-light;
    }

    &:before {
      content: $icon-info-thin;
      @include icon-font-thin();
      font-size: em(21);
    }
  }

  .main-content {
    background: $formBlock;

    @include bp-mf-tablet {
      border-top: none;
    }
  }

  ::v-deep .input-container {
    position: static;

    .placeholder {
      height: 48px;
      padding: 16px;
      font-size: em(16);
    }
  }

  .wrap {
    padding: 24px;

    @include bp-mf-desktop {
      padding: 24px 0 24px;
    }

    &.contain {
      @include bp-mf-desktop {
        padding-bottom: 50px !important;
      }
    }

    &.terms {
      padding: 0 0 36px 0;

      .terms-and-conditions-block {
        background-color: #ebebeb;
      }
    }

    &.reset {
      padding: 40px 24px 0 24px;
    }
  }

  h1 {
    margin: 0 0 16px 0;
    color: $blue-dark;
    font-size: em(32);
    font-weight: 500;
    line-height: 38px;

    @include bp-mf-desktop {
      font-size: em(48);
      line-height: 57px;
      margin: 0 0 20px 0;
    }
  }

  .description {
    display: none;

    @include bp-mf-desktop {
      display: block;
      font-size: em(24);
      line-height: 36px;
    }
  }

  .destination-container {
    margin-bottom: 36px;

    @include bp-mf-desktop {
      flex: 7;
    }

    &.map {
      @include bp-mf-tablet {
        background: white;
        padding: 20px;
      }

      ::v-deep .destination {
        @include bp-mf-tablet {
          padding: 28px 0 0 0 !important;
        }
      }
    }

    .destination {
      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  .filter-group {
    display: none;
    width: 100%;
    border-radius: 6px 6px 0 0;
    background-color: #dddde4;

    @include bp-mf-tablet {
      background-color: $formBlock;
      width: 100%;
      padding-right: 28px;
    }

    @include bp-mf-desktop {
      flex: 2;
      max-width: 271px;
      display: block;
    }

    .wrap {
      background-color: unset;
      border: none;
      padding: 0;
    }
  }

  .content-wrap {
    display: block;

    @include bp-mf-desktop {
      display: flex;
    }
  }

  .map-content-wrap {
    @include bp-mf-tablet {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      width: 100%;
    }

    .inner {
      position: relative;
      margin: 0 auto;
      max-width: 1600px;
    }
  }

  .filter {
    mask-image: url('~assets/svg/filter.svg');
    background-color: $blue-dark;
    margin-right: 12px;
    min-width: 24px;
    min-height: 24px;
    max-width: 24px;
    max-height: 24px;
  }

  .title-wrap {
    display: block;

    @include bp-mf-desktop {
      display: grid;
      column-gap: 20px;
      grid-template-columns: 8fr 4fr;
    }
  }

  .selector {
    &-wrap {
      align-self: flex-end;
      display: flex;
      justify-content: space-between;
      justify-self: flex-end;
      margin-top: 16px;

      @include bp-mf-desktop {
        display: block;
        margin-top: 0;
      }
    }

    &-text {
      display: none;

      @include bp-mf-desktop {
        width: 100%;
        display: block;
      }
    }

    &-tab {
      display: flex;
      align-items: center;

      .item {
        align-items: center;
        border: 1px solid $blue-medium;
        color: $blue-medium;
        cursor: pointer;
        display: flex;
        font-size: rem(14, 16);
        height: rem(44, 16);
        padding: 10px;
        text-align: center;
        width: auto;

        @include bp-mf-desktop {
          padding: 0px 11px;
          min-width: 116px;
        }
      }

      .map {
        border-radius: 4px 0 0 4px;
      }

      .list {
        border-radius: 0 4px 4px 0;
      }

      .icon {
        min-height: 24px;
        min-width: 24px;
        max-height: 24px;
        max-width: 24px;

        @include bp-mf-desktop {
          margin-right: 3px;
        }

        &.list {
          mask-image: url('~assets/svg/list.svg');
          background-color: $blue-medium;
        }

        &.pin {
          mask-image: url('~assets/svg/pin.svg');
          background-color: $blue-medium;
        }
      }

      .active {
        background: $blue-medium;
        color: white;

        .icon {
          &.list,
          &.pin {
            background-color: white;
          }
        }
      }
    }
  }

  .filter-wrapper {
    align-items: center;
    color: $blue-dark;
    display: flex;
    font-size: rem(14, 16);
    font-weight: 500;
    line-height: 16px;
    margin-bottom: 19px;
  }

  .airports {
    margin-bottom: 16px !important;
  }

  .autocomplete ::v-deep {
    .multiselect {
      margin-bottom: 0 !important;
    }

    .selected {
      font-weight: 700;
    }
  }

  .desktop-filters {
    display: none;

    @include bp-mf-desktop {
      display: block;
    }
  }

  .destinations-wrap {
    background-color: white;
  }

  .destinations-silo ::v-deep {
    .field-group {
      @include bp-mf-tablet {
        margin-bottom: 20px;
      }
    }
  }

  .filters-trigger {
    align-items: center;
    border-radius: 4px;
    border: 1px solid $grey-border;
    color: $blue-medium;
    cursor: pointer;
    display: flex;
    font-size: rem(16, 16);
    font-weight: 500;
    height: 44px;
    justify-content: center;
    padding: 10px 23px;

    @include bp-mf-desktop {
      display: none;
    }
  }

  .map-with-filters {
    position: relative;

    @include bp-mf-tablet {
      display: flex;
    }

    .filters-trigger {
      display: none !important;

      @include bp-mf-desktop {
        border-radius: 6px;
        color: $blue-dark;
        display: flex !important;
        font-size: rem(14, 16);
        padding: 10px 14px !important;
      }
    }

    .floating-filters {
      background: white;
      border-radius: 6px;
      display: none !important;
      left: 24px;
      position: absolute;
      top: 40px;

      @include bp-mf-desktop {
        display: block !important;
      }

      &.open {
        width: 290px;
        padding: 17px 20px;
      }
    }

    .filter-wrapper {
      @include bp-mf-desktop {
        justify-content: space-between;
      }

      .close-icon ::v-deep {
        position: static !important;
      }
    }

    .filters-title-wrap {
      align-items: center;
      display: flex;
    }

    ::v-deep .map-container {
      width: 100% !important;
    }
  }

  .destination-drawer {
    background: $blue-bg;
    padding: 17px 20px 24px;

    @include bp-mf-tablet {
      position: absolute;
      right: 0;
      top: 0;
      max-width: 300px;
      width: 100%;
      background: white;
      height: 86vh;
      padding: 0;
    }

    @include bp-mf-desktop {
      max-width: 360px;
    }
  }
</style>
<style lang="scss" rel="stylesheet/scss">
  @import '~assets/styles/common.scss';
  @import '~assets/styles/components/tooltip.scss';

  .js-tooltip.js-calendar-tooltip {
    @include tooltip();

    position: absolute;
    z-index: 99999 !important;
    max-width: 270px;
    width: 100%;
    text-align: center;
    border: 1px solid $scn-gray-border;
    background-color: white;
    color: $blue-dark;
    border-radius: 4px;
    margin-bottom: 10px !important;

    .tooltip-cover {
      position: relative;
      z-index: 99999;

      .tooltip-arrow {
        border-color: rgba($scn-gray-border, 1) transparent transparent;
        border-width: 8px 8px 0;

        &:after {
          content: '';
          position: relative;
          display: block;
          top: -9px;
          left: -7px;
          border-color: white transparent transparent;
          border-style: solid;
          border-width: 7px 7px 0;
        }
      }
    }
  }

  .wrap.contain {
    margin-top: 0;
    @include bp-mf-desktop {
      margin-top: 60px;
    }
  }
</style>